import { useState, useEffect, useRef } from "react";
import {
  fetchNoticeList, fetchNoticeDetail, createNotice, updateNotice, deleteNotice,
  fetchNoticeFiles, uploadNoticeFiles, deleteNoticeFile   // ← 추가
} from "../api/noticeApi";
import { resolveImageUrl } from "../utils/resolveImageUrl";  // ← 이미 있는 유틸 재사용
import Toast from "../components/Toast";
import { getCodeList } from "../api/codeApi";

// ===================== 상세/등록 =====================
const DetailPage = ({ item, onBack, onSave, isNew, isAdmin }) => {
  const [form, setForm] = useState({
    notice_year: new Date().getFullYear().toString(),
    title: '', content: '', img_data: null,
    noti_start_dt: '', noti_end_dt: ''
  });
  const [preview, setPreview] = useState(null);
  const [toast,   setToast]   = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [files,        setFiles]        = useState([]);   // 서버에 이미 업로드된 첨부파일 목록
  const [pendingFiles,  setPendingFiles] = useState([]);   // 신규 등록 시, 저장 전 선택만 해둔 파일들
  const [fileUploading, setFileUploading] = useState(false);
  const fileRef = useRef(null);
  const docRef  = useRef(null);   // ← 문서 첨부용 input ref 추가

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (item) {
      setForm({
        notice_year  : item.notice_year,
        title        : item.title,
        content      : item.content,
        img_data     : item.img_data,
        noti_start_dt: item.noti_start_dt,
        noti_end_dt  : item.noti_end_dt
      });
      setPreview(item.img_data);
      loadFiles(item);
    }
  }, [item]);

  const loadFiles = async (target) => {
    const res = await fetchNoticeFiles(target.notice_year, target.seq_no);
    if (res.success) setFiles(res.data);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { showToast('❌ 이미지 크기는 1MB 이하여야 합니다.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { set('img_data', ev.target.result); setPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.title)         { showToast('❌ 제목을 입력해주세요.'); return; }
    if (!form.content)       { showToast('❌ 내용을 입력해주세요.'); return; }
    if (!form.noti_start_dt) { showToast('❌ 알림 시작일을 입력해주세요.'); return; }
    if (!form.noti_end_dt)   { showToast('❌ 알림 종료일을 입력해주세요.'); return; }

    if (isNew && pendingFiles.length > 0) {
      // onSave가 성공하면 부모(NoticePage)에서 목록으로 돌아가므로,
      // 여기서는 먼저 등록 API를 직접 호출해 seq_no를 받아 파일까지 업로드한 뒤 onSave 흐름을 이어감
      onSave(form, pendingFiles);
    } else {
      onSave(form);
    }
  };

  // 첨부파일(문서) 선택
  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const oversize = selectedFiles.find(f => f.size > 3 * 1024 * 1024);
    if (oversize) { showToast(`❌ "${oversize.name}"이(가) 3MB를 초과합니다.`); e.target.value = ''; return; }

    if (isNew) {
      // 아직 저장 전이면, 화면에만 선택 목록으로 표시해두고 등록 시 함께 업로드
      setPendingFiles(prev => [...prev, ...selectedFiles]);
    } else {
      // 이미 존재하는 공지사항이면 즉시 업로드
      setFileUploading(true);
      try {
        const res = await uploadNoticeFiles(item.notice_year, item.seq_no, selectedFiles);
        if (res.success) { showToast('✅ 파일이 업로드되었습니다.'); await loadFiles(item); }
        else showToast(`❌ ${res.message}`);
      } catch (err) {
        showToast('❌ 파일 업로드 중 오류가 발생했습니다.');
      } finally {
        setFileUploading(false);
      }
    }
    e.target.value = '';
  };

  // 대기 중(저장 전) 파일 제거
  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // 이미 업로드된 파일 삭제
  const handleFileDelete = async (file_no) => {
    if (!window.confirm('첨부파일을 삭제하시겠습니까?')) return;
    const res = await deleteNoticeFile(item.notice_year, item.seq_no, file_no);
    if (res.success) { showToast('✅ 파일이 삭제되었습니다.'); await loadFiles(item); }
    else showToast(`❌ ${res.message}`);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  };

  const handleDelete = async () => {
    try {
      const res = await deleteNotice(item.notice_year, item.seq_no);
      if (res.success) { showToast('✅ 삭제되었습니다.'); setTimeout(() => onBack(), 500); }
      else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 삭제 실패'); }
    finally { setShowDeleteModal(false); }
  };

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />
      <button style={styles.backBtn} onClick={onBack}>← 목록으로</button>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>{isNew ? '📢 공지사항 등록' : '📢 공지사항 상세'}</h1>
      </div>

      <div style={{ ...styles.card, padding: 32 }}>
        <div style={detailStyles.grid}>
          {/* 연도 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>연도 <span style={{ color: "#ff4d4f" }}>*</span></label>
            {isNew
              ? <input style={detailStyles.input} value={form.notice_year}
                  onChange={e => set('notice_year', e.target.value)} placeholder="예) 2026" maxLength={4} />
              : <input style={{ ...detailStyles.input, background: "#f5f5f5", color: "#aaa" }}
                  value={form.notice_year} disabled />
            }
          </div>
          {/* 알림 기간 */}
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>알림 시작일 <span style={{ color: "#ff4d4f" }}>*</span></label>
            <input style={detailStyles.input} type="date" value={form.noti_start_dt}
              onChange={e => set('noti_start_dt', e.target.value)} readOnly={!isAdmin} />
          </div>
          <div style={detailStyles.field}>
            <label style={detailStyles.label}>알림 종료일 <span style={{ color: "#ff4d4f" }}>*</span></label>
            <input style={detailStyles.input} type="date" value={form.noti_end_dt}
              onChange={e => set('noti_end_dt', e.target.value)} readOnly={!isAdmin} />
          </div>
        </div>

        {/* 제목 */}
        <div style={{ ...detailStyles.field, marginTop: 12 }}>
          <label style={detailStyles.label}>제목 <span style={{ color: "#ff4d4f" }}>*</span></label>
          <input style={detailStyles.input} value={form.title}
            onChange={e => set('title', e.target.value)} placeholder="제목 입력"
            readOnly={!isAdmin} />
        </div>

        {/* 내용 */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1677ff", borderLeft: "3px solid #1677ff", paddingLeft: 10, marginBottom: 8 }}>내용</div>
          <textarea
            style={{ width: "100%", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "12px", fontSize: 14, outline: "none", resize: "vertical", minHeight: 160, boxSizing: "border-box", background: !isAdmin ? "#f5f5f5" : "#fff" }}
            value={form.content}
            onChange={e => set('content', e.target.value)}
            placeholder="내용 입력"
            readOnly={!isAdmin}
          />
        </div>

        {/* 이미지 */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1677ff", borderLeft: "3px solid #1677ff", paddingLeft: 10, marginBottom: 12 }}>
            첨부 이미지 (1MB 이하, 선택)
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            <div style={{ width: 200, height: 160, border: "2px dashed #e0e0e0", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#fafafa", flexShrink: 0 }}>
              {preview
                ? <img src={preview} alt="첨부이미지" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                : <span style={{ fontSize: 13, color: "#bbb" }}>이미지 없음</span>
              }
            </div>
            {isAdmin && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input type="file" accept="image/*" ref={fileRef} style={{ display: "none" }} onChange={handleImageUpload} />
                <button style={{ background: "#fff", border: "1.5px solid #1677ff", color: "#1677ff", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                  onClick={() => fileRef.current.click()}>🖼️ 이미지 선택</button>
                {preview && (
                  <button style={{ background: "#fff2f0", border: "1.5px solid #ff4d4f", color: "#ff4d4f", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                    onClick={() => { set('img_data', null); setPreview(null); }}>🗑 이미지 제거</button>
                )}
                <span style={{ fontSize: 12, color: "#aaa" }}>JPG, PNG, GIF 지원 / 최대 1MB</span>
              </div>
            )}
          </div>
        </div>

        {/* 첨부파일 (신규) */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1677ff", borderLeft: "3px solid #1677ff", paddingLeft: 10, marginBottom: 12 }}>
            첨부파일 (파일당 3MB 이하, 여러 개 선택 가능)
          </div>

          {isAdmin && (
            <div style={{ marginBottom: 12 }}>
              <input type="file" multiple ref={docRef} style={{ display: "none" }} onChange={handleFileSelect} />
              <button style={{ background: "#fff", border: "1.5px solid #1677ff", color: "#1677ff", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: fileUploading ? "not-allowed" : "pointer", opacity: fileUploading ? 0.6 : 1 }}
                onClick={() => docRef.current.click()} disabled={fileUploading}>
                {fileUploading ? '업로드 중...' : '📎 파일 선택'}
              </button>
            </div>
          )}

          {/* 저장 전 대기 중인 파일 목록 (신규 등록 시) */}
          {pendingFiles.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {pendingFiles.map((f, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f0f7ff", borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: "#1a1a2e" }}>📄 {f.name} <span style={{ color: "#aaa", fontSize: 12 }}>({formatFileSize(f.size)})</span></span>
                  <button style={{ background: "none", border: "none", color: "#ff4d4f", cursor: "pointer", fontSize: 13 }}
                    onClick={() => removePendingFile(idx)}>삭제</button>
                </div>
              ))}
              <div style={{ fontSize: 12, color: "#faad14" }}>※ "등록" 버튼을 눌러야 최종 저장됩니다.</div>
            </div>
          )}

          {/* 이미 업로드된 파일 목록 */}
          {files.length === 0 && pendingFiles.length === 0 ? (
            <div style={{ fontSize: 13, color: "#bbb" }}>첨부된 파일이 없습니다.</div>
          ) : (
              files.map(f => (
                <div key={f.file_no} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#fafafa", border: "1px solid #eee", borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                  <a href={resolveImageUrl(`/api/notice/${item.notice_year}/${item.seq_no}/files/${f.file_no}/download?branch_code=${item.branch_code}&division_code=${item.division_code}`)}
                    style={{ color: "#1677ff", textDecoration: "none", fontWeight: 600 }}>
                    📄 {f.org_file_name} <span style={{ color: "#aaa", fontWeight: 400, fontSize: 12 }}>({formatFileSize(f.file_size)})</span>
                  </a>
                  {isAdmin && (
                    <button style={{ background: "none", border: "none", color: "#ff4d4f", cursor: "pointer", fontSize: 13 }}
                      onClick={() => handleFileDelete(f.file_no)}>삭제</button>
                  )}
                </div>
              ))
          )}
        </div>

        {/* 버튼 */}
        {isAdmin && (
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 32, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}>
            {!isNew && (
              <button style={styles.deleteBtn} onClick={() => setShowDeleteModal(true)}>🗑 삭제</button>
            )}
            <button style={styles.editBtn} onClick={handleSubmit}>
              {isNew ? '✅ 등록' : '✏️ 수정'}
            </button>
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 380, margin: "0 16px", padding: "40px 28px 28px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e", margin: "0 0 10px" }}>공지사항을 삭제하시겠습니까?</h3>
            <p style={{ fontSize: 13, color: "#999", margin: "0 0 28px" }}>삭제된 데이터는 복구할 수 없습니다.</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "10px 24px", fontSize: 14, color: "#666", cursor: "pointer", fontWeight: 600 }}
                onClick={() => setShowDeleteModal(false)}>취소</button>
              <button style={{ background: "#ff4d4f", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, color: "#fff", cursor: "pointer", fontWeight: 700 }}
                onClick={handleDelete}>삭제</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== 목록 =====================
export default function NoticePage({ onLogout, isAdmin }) {
  const [page,       setPage]       = useState('list');
  const [list,       setList]       = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [isNew,      setIsNew]      = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [yearList, setYearList] = useState([]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const loadList = async (year) => {
    setLoading(true);
    try {
      const res = await fetchNoticeList(year);
      if (res.success) setList(res.data);
      else if (res.message === '토큰이 만료되었습니다. 다시 로그인해주세요.') onLogout();
    } catch (err) { showToast('❌ 목록 조회 실패'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadList(yearFilter); }, [yearFilter]);
  useEffect(() => {
    const codes = getCodeList('YEAR_LIST');
    setYearList(codes);
    if (codes.length > 0 && !codes.find(c => c.code === new Date().getFullYear().toString())) {
      setYearFilter(codes[0].code);
    }
  }, []);

  const handleSave = async (form, pendingFiles) => {
    try {
      const res = isNew
        ? await createNotice(form)
        : await updateNotice(selected.notice_year, selected.seq_no, form);

      if (res.success) {
        // 신규 등록 + 대기 중인 첨부파일이 있으면, 방금 발급된 seq_no로 업로드
        if (isNew && pendingFiles && pendingFiles.length > 0) {
          const uploadRes = await uploadNoticeFiles(res.notice_year, res.seq_no, pendingFiles);
          if (!uploadRes.success) showToast(`⚠️ 공지사항은 등록되었으나 파일 업로드에 실패했습니다: ${uploadRes.message}`);
        }
        showToast(isNew ? '✅ 등록되었습니다.' : '✅ 수정되었습니다.');
        loadList(yearFilter);
        if (isNew) {
          setPage('list');
        } else {
          await loadDetail(selected);
        }
      } else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 저장 실패'); }
  };

  const loadDetail = async (item) => {
    try {
      const res = await fetchNoticeDetail(item.notice_year, item.seq_no);
      if (res.success) { setSelected(res.data); setIsNew(false); setPage('detail'); }
    } catch (err) { showToast('❌ 상세 조회 실패'); }
  };

  if (page === 'detail') {
    return (
      <>
        <Toast msg={toast} />
        <DetailPage
          item={selected}
          onBack={() => { loadList(yearFilter); setPage('list'); }}
          onSave={handleSave}
          isNew={isNew}
          isAdmin={isAdmin}
        />
      </>
    );
  }

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>📢 공지사항</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select style={styles.select} value={yearFilter}
            onChange={e => setYearFilter(e.target.value)}>
            <option value="">전체 연도</option>
            {yearList.map(y => (
              <option key={y.code} value={y.code}>{y.code_nm}</option>
            ))}
          </select>
          {isAdmin && (
            <button style={styles.registerBtn}
              onClick={() => { setSelected(null); setIsNew(true); setPage('detail'); }}>
              ＋ 공지 등록
            </button>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.tableHeader}>
          {["연번", "제목", "알림기간", "등록일"].map(h => (
            <div key={h} style={styles.th}>{h}</div>
          ))}
        </div>
        {loading ? (
          <div style={styles.empty}>⏳ 불러오는 중...</div>
        ) : list.length === 0 ? (
          <div style={styles.empty}>등록된 공지사항이 없습니다.</div>
        ) : list.map((item, i) => (
          <div key={i} style={styles.tableRow}
            onClick={() => loadDetail(item)}
            onMouseEnter={e => e.currentTarget.style.background = "#f8faff"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
            <div style={styles.td}>{item.seq_no}</div>
            <div style={{ ...styles.td, fontWeight: 600, color: "#1677ff", cursor: "pointer" }}>{item.title}</div>
            <div style={styles.td}>{item.noti_start_dt} ~ {item.noti_end_dt}</div>
            <div style={styles.td}>{item.reg_dt?.slice(0, 10)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  wrap        : { background: "#f5f7fa", padding: 24, fontFamily: "'Noto Sans KR', sans-serif" },
  pageHeader  : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  pageTitle   : { fontSize: 22, fontWeight: 800, color: "#1a1a2e", margin: 0 },
  select      : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 14, outline: "none", background: "#fff", cursor: "pointer" },
  registerBtn : { background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  card        : { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  tableHeader : { display: "grid", gridTemplateColumns: "0.5fr 3fr 2fr 1.2fr", padding: "12px 20px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" },
  th          : { fontSize: 12, fontWeight: 700, color: "#888" },
  tableRow    : { display: "grid", gridTemplateColumns: "0.5fr 3fr 2fr 1.2fr", padding: "14px 20px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", transition: "background 0.15s", alignItems: "center" },
  td          : { fontSize: 14, color: "#1a1a2e" },
  empty       : { textAlign: "center", padding: "60px 0", color: "#bbb", fontSize: 14 },
  backBtn     : { background: "none", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: "#666", cursor: "pointer", marginBottom: 12, display: "block" },
  editBtn     : { background: "#fff", border: "1.5px solid #1677ff", color: "#1677ff", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  deleteBtn   : { background: "#fff2f0", border: "1.5px solid #ff4d4f", color: "#ff4d4f", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
};

const detailStyles = {
  grid  : { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
  field : { display: "flex", flexDirection: "row", alignItems: "center", background: "#fafafa", borderRadius: 8, border: "1.5px solid #e0e0e0", overflow: "hidden" },
  label : { fontSize: 13, fontWeight: 600, color: "#666", background: "#f0f4ff", padding: "12px 16px", minWidth: 110, whiteSpace: "nowrap", borderRight: "1.5px solid #e0e0e0" },
  input : { border: "none", borderRadius: 0, padding: "12px 16px", fontSize: 14, outline: "none", background: "transparent", color: "#1a1a2e", width: "100%", boxSizing: "border-box" },
};